/**
 * BlogArticlePage - Pagină individuală pentru un articol blog
 */
import { useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Calendar, Clock as ClockIcon, ArrowLeft, ArrowRight, Phone, Mail, Tag } from "lucide-react";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { getArticleBySlug, blogArticles, formatArticleDate } from "@/data/blogArticles";

export default function BlogArticlePage() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  // Sanitizează conținutul articolului înainte de render (defense-in-depth)
  const safeContent = useMemo(() => {
    if (!article?.content) return "";
    return DOMPurify.sanitize(article.content, {
      ALLOWED_TAGS: ["h1", "h2", "h3", "h4", "p", "ul", "ol", "li", "strong", "em", "a", "br", "blockquote", "code"],
      ALLOWED_ATTR: ["href", "target", "rel"],
      ALLOW_DATA_ATTR: false,
    });
  }, [article]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Blog e-Tesa`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", article.excerpt);
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute("content", article.title);
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute("content", article.excerpt);
      const ogImg = document.querySelector('meta[property="og:image"]');
      if (ogImg) ogImg.setAttribute("content", "https://e-tesa.ro/og/article.jpg");
      const twImg = document.querySelector('meta[name="twitter:image"]');
      if (twImg) twImg.setAttribute("content", "https://e-tesa.ro/og/article.jpg");
      const twTitle = document.querySelector('meta[name="twitter:title"]');
      if (twTitle) twTitle.setAttribute("content", article.title);
      const twDesc = document.querySelector('meta[name="twitter:description"]');
      if (twDesc) twDesc.setAttribute("content", article.excerpt);
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute("href", `https://e-tesa.ro/blog/${article.slug}`);
    }
    // scroll sus la deschidere
    window.scrollTo(0, 0);
  }, [article]);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  // Articol related (random alt)
  const related = blogArticles.filter((a) => a.slug !== article.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-blue-50/40 text-slate-800">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/etesa-logo.png" alt="e-Tesa" className="h-9 w-auto" />
          </Link>
          <nav className="flex items-center gap-4 text-sm font-medium text-slate-600">
            <Link to="/blog" className="hover:text-teal-600 transition-colors">← Blog</Link>
            <Link to="/login" className="px-4 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-blue-500 text-white hover:from-teal-600 hover:to-blue-600">
              Intră în cont
            </Link>
          </nav>
        </div>
      </header>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 pb-16">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-600 mb-6">
          <ArrowLeft className="w-4 h-4" /> Înapoi la blog
        </Link>

        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-4">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-100/70 text-teal-700 rounded-full font-medium">
            <Tag className="w-3 h-3" /> {article.category}
          </span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {formatArticleDate(article.date)}
          </span>
          <span className="inline-flex items-center gap-1">
            <ClockIcon className="w-3.5 h-3.5" />
            {article.readingTime} de citit
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15] mb-6">
          {article.title}
        </h1>

        <p className="text-lg text-slate-600 leading-relaxed mb-10 border-l-4 border-teal-500 pl-4 italic">
          {article.excerpt}
        </p>

        <div
          className="prose-article text-slate-700"
          dangerouslySetInnerHTML={{ __html: safeContent }}
        />

        {/* CTA */}
        <div className="mt-12 p-6 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl text-white">
          <h3 className="text-xl font-bold mb-2">Vrei să încerci e-Tesa?</h3>
          <p className="text-teal-50 text-sm mb-4">Deschide cont gratuit și primești 200 credite bonus pentru a proba toate funcțiile.</p>
          <Button
            onClick={() => (window.location.href = "/login?register=1")}
            className="bg-white text-teal-700 hover:bg-slate-100 rounded-xl"
            data-testid="article-cta"
          >
            Începe acum
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-xl font-bold mb-6">Alte articole</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                className="group bg-white/70 backdrop-blur rounded-2xl border border-white/80 p-5 hover:shadow-lg transition-all"
              >
                <div className="text-xs text-teal-600 font-medium mb-1">{a.category}</div>
                <h3 className="font-semibold leading-snug mb-2 group-hover:text-teal-700">{a.title}</h3>
                <p className="text-sm text-slate-600 line-clamp-2">{a.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <img src="/etesa-logo.png" alt="e-Tesa" className="h-10 w-auto mb-3" />
            <p className="text-sm text-slate-400">Platforma all-in-one pentru firmele de construcții din România.</p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">Contact</h4>
            <a href="tel:0755898253" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-2">
              <Phone className="w-4 h-4" /> 0755 898 253
            </a>
            <a href="mailto:contact@e-tesa.ro" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white">
              <Mail className="w-4 h-4" /> contact@e-tesa.ro
            </a>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">Legal</h4>
            <Link to="/legal?tab=privacy" className="block text-sm text-slate-400 hover:text-white mb-2">Confidențialitate</Link>
            <Link to="/legal?tab=terms" className="block text-sm text-slate-400 hover:text-white">Termeni</Link>
          </div>
        </div>
        <div className="border-t border-slate-800 py-4 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} e-Tesa. Toate drepturile rezervate.
        </div>
      </footer>
    </div>
  );
}
